/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useRef } from 'react';

import type { AnimationItem } from 'lottie-web';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	container: {
		width: '100%',
		height: '100%',
		borderRadius: token('radius.xlarge'),
		overflow: 'hidden',
	},
});

export type LottieRendererProps = {
	/**
	 * URL of the Lottie JSON file to fetch on initial client render.
	 */
	lottieSrc: string;
	/**
	 * Whether the animation should currently be playing.
	 */
	isPlaying: boolean;
	/**
	 * Accessible label; mirrors the parent `MotionPlayer`'s `aria-label`.
	 */
	label: string;
	/**
	 * Id of an external element (e.g. a caption) to associate with the asset
	 * via `aria-describedby`.
	 */
	describedById?: string;
	/**
	 * Called when a loop completes; parent resets "playing" and rewinds.
	 */
	onLoopComplete: () => void;
	/**
	 * Called once the animation data is ready to play.
	 */
	onLoaded: () => void;
	/**
	 * Called when Lottie cannot load the animation.
	 */
	onLoadFailed: () => void;
};

/**
 * __LottieRenderer__
 *
 * Lottie-specific renderer for `MotionPlayer`. Its initial effect lazily
 * imports the player and fetches the animation JSON, paused.
 */
function LottieRenderer({
	lottieSrc,
	isPlaying,
	label,
	describedById,
	onLoopComplete,
	onLoaded,
	onLoadFailed,
}: LottieRendererProps): React.JSX.Element {
	const lottieRef = useRef<AnimationItem | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	/* Expand SVG filter bounds so Lottie exports that use filters don't
	 * get clipped at the edges. */
	const patchSvgFilterBounds = useCallback(() => {
		const container = containerRef.current;
		if (!container) {
			return;
		}
		container.querySelectorAll('filter').forEach((filter) => {
			filter.setAttribute('x', '-10%');
			filter.setAttribute('y', '-10%');
			filter.setAttribute('width', '120%');
			filter.setAttribute('height', '120%');
		});
	}, []);

	const handleLoopComplete = useCallback(() => {
		onLoopComplete();
		/* `true` = arg is a frame number, not milliseconds. Stays correct
		 * for animations exported with a non-zero in-point. */
		lottieRef.current?.goToAndStop(0, true);
	}, [onLoopComplete]);

	const handleDomLoaded = useCallback(() => {
		patchSvgFilterBounds();
		onLoaded();
	}, [onLoaded, patchSvgFilterBounds]);

	useEffect(() => {
		if (isPlaying) {
			lottieRef.current?.play();
		} else {
			lottieRef.current?.pause();
		}
	}, [isPlaying]);

	useEffect(() => {
		if (!containerRef.current) {
			return;
		}

		let disposed = false;
		let animation: AnimationItem | null = null;

		import(/* webpackChunkName: "lottie-light" */ 'lottie-web/build/player/lottie_light')
			.then(({ default: lottie }) => {
				if (disposed || !containerRef.current) {
					return;
				}

				animation = lottie.loadAnimation({
					container: containerRef.current,
					renderer: 'svg',
					path: lottieSrc,
					loop: true,
					autoplay: false,
					rendererSettings: { progressiveLoad: true },
				});
				lottieRef.current = animation;
				// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners -- Lottie AnimationItem is not a DOM EventTarget; listeners are removed during cleanup below.
				animation.addEventListener('DOMLoaded', handleDomLoaded);
				// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners -- Lottie AnimationItem is not a DOM EventTarget; listeners are removed during cleanup below.
				animation.addEventListener('loopComplete', handleLoopComplete);
				// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners -- Lottie AnimationItem is not a DOM EventTarget; listeners are removed during cleanup below.
				animation.addEventListener('data_failed', onLoadFailed);
				animation.goToAndStop(0, true);
			})
			.catch(() => {
				if (!disposed) {
					onLoadFailed();
				}
			});

		return () => {
			disposed = true;
			if (animation) {
				// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners -- Lottie AnimationItem is not a DOM EventTarget.
				animation.removeEventListener('DOMLoaded', handleDomLoaded);
				// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners -- Lottie AnimationItem is not a DOM EventTarget.
				animation.removeEventListener('loopComplete', handleLoopComplete);
				// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners -- Lottie AnimationItem is not a DOM EventTarget.
				animation.removeEventListener('data_failed', onLoadFailed);
			}
			animation?.destroy();
			if (lottieRef.current === animation) {
				lottieRef.current = null;
			}
		};
	}, [handleDomLoaded, handleLoopComplete, lottieSrc, onLoadFailed]);

	return (
		<div
			ref={containerRef}
			css={styles.container}
			role="img"
			aria-label={label}
			aria-describedby={describedById}
		/>
	);
}

export default LottieRenderer;
